import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  Paper,
  Chip,
  alpha,
  Fade,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
} from '@mui/material';
import {
  ArrowForward,
  ArrowBack,
  CheckCircle,
  Lock,
  SwapHoriz,
  Speed,
  GitHub,
  Article,
  Security,
  Shield,
  VisibilityOff,
  Public,
  AccountBalance,
  Groups,
  Fingerprint,
  Timer,
  Language,
  LinkOff,
  AutoAwesome,
} from '@mui/icons-material';
import Header from '../components/Header';
import { getChainLogo } from '../components/ChainLogos';
import { useThemeMode } from '../hooks/useThemeMode';

const PitchDeck = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();
  const { isDark } = useThemeMode();

  const slides = [
    {                                                              // 1
      id: 'title',
      title: 'InterNull',
      subtitle: 'Privacy Infrastructure for the Multi-Chain Era',
      tagline: 'Break the on-chain link between deposit and withdrawal using distributed threshold cryptography. No trusted party. No single point of failure.',
    },
    {                                                              // 2
      id: 'problem',
      title: 'The Privacy Crisis in Web3',
      content: 'problem',
    },
    {                                                              // 3
      id: 'solution',
      title: 'Our Solution',
      content: 'solution',
    },
    {                                                              // 4
      id: 'protocol-in-action',
      title: 'Split, Route, Disappear',
      content: 'protocolInAction',
    },
    {                                                              // 5
      id: 'how-it-works',
      title: 'How It Works',
      content: 'howItWorks',
    },
    {                                                              // 6
      id: 'architecture',
      title: 'Decentralized 5-Node Network',
      content: 'architecture',
    },
    {                                                              // 7
      id: 'security',
      title: 'Zero Trust, Full Privacy',
      content: 'security',
    },
    {                                                              // 8
      id: 'competitive',
      title: 'How InterNull Compares',
      content: 'competitive',
    },
    {                                                              // 9
      id: 'agents',
      title: 'Built for AI Agents',
      content: 'agents',
    },
    {                                                              // 10
      id: 'use-cases',
      title: 'Who Needs InterNull?',
      content: 'useCases',
    },
    {                                                              // 11 (moved from 7)
      id: 'market',
      title: 'Total Addressable Market',
      content: 'market',
    },
    {                                                              // 12
      id: 'traction',
      title: 'Live on Testnet',
      content: 'traction',
    },
    {                                                              // 13
      id: 'roadmap',
      title: "What's Next",
      content: 'roadmap',
    },
    {                                                              // 14
      id: 'team',
      title: 'Built by InterNull',
      content: 'team',
    },
    {                                                              // 15
      id: 'cta',
      title: 'Privacy is Not Optional. It\'s Infrastructure.',
      content: 'cta',
    },
  ];

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const SlideContainer = ({ children }) => (
    <Fade in timeout={600}>
      <Box
        sx={{
          minHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: 6,
        }}
      >
        {children}
      </Box>
    </Fade>
  );

  const renderSlideContent = () => {
    const slide = slides[currentSlide];

    switch (slide.content) {
      case 'problem':
        return (
          <SlideContainer>
            <Typography variant="h3" fontWeight="700" gutterBottom sx={{ color: '#ef4444', mb: 4 }}>
              {slide.title}
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Card sx={{
                  bgcolor: alpha('#ef4444', 0.1),
                  border: `2px solid ${alpha('#ef4444', 0.3)}`,
                  height: '100%'
                }}>
                  <CardContent>
                    <VisibilityOff sx={{ fontSize: 48, color: '#ef4444', mb: 2 }} />
                    <Typography variant="h5" fontWeight="600" gutterBottom>
                      Complete Transparency = Zero Privacy
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                      Every transaction on blockchain is public, permanent, and traceable. Your entire financial history is exposed.
                    </Typography>
                    <List dense>
                      <ListItem><Typography>• Wallet balances visible to everyone</Typography></ListItem>
                      <ListItem><Typography>• Transaction history permanently recorded</Typography></ListItem>
                      <ListItem><Typography>• Identity easily linked to addresses</Typography></ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{
                  bgcolor: alpha('#f59e0b', 0.1),
                  border: `2px solid ${alpha('#f59e0b', 0.3)}`,
                  height: '100%'
                }}>
                  <CardContent>
                    <Fingerprint sx={{ fontSize: 48, color: '#f59e0b', mb: 2 }} />
                    <Typography variant="h5" fontWeight="600" gutterBottom>
                      Real-World Consequences
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon><CheckCircle sx={{ color: '#f59e0b' }} /></ListItemIcon>
                        <ListItemText
                          primary="Business Intelligence Leaks"
                          secondary="Competitors track your treasury movements"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckCircle sx={{ color: '#f59e0b' }} /></ListItemIcon>
                        <ListItemText
                          primary="Personal Security Risks"
                          secondary="Large holdings make you a target"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckCircle sx={{ color: '#f59e0b' }} /></ListItemIcon>
                        <ListItemText
                          primary="Price Manipulation"
                          secondary="Whales front-run your trades"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </SlideContainer>
        );

      case 'solution':
        return (
          <SlideContainer>
            <Typography variant="h3" fontWeight="700" gutterBottom sx={{ color: '#4ade80', mb: 2 }}>
              {slide.title}
            </Typography>

            {/* Deferred Intents Explanation */}
            <Paper sx={{
              p: 4,
              mb: 4,
              background: isDark
                ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.15) 100%)'
                : 'linear-gradient(135deg, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0.15) 100%)',
              border: isDark ? '2px solid rgba(255, 255, 255, 0.4)' : '2px solid rgba(0, 0, 0, 0.4)',
            }}>
              <Typography variant="h5" fontWeight="700" gutterBottom sx={{ color: isDark ? '#e5e5e5' : '#171717' }}>
                🎯 Deferred Privacy Intents
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Express your cross-chain intent. Receive cryptographic keys that tokenize that intent. Execute whenever you want, on any chain, with complete privacy.
              </Typography>

              {/* Intent Flow Visualization */}
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <Paper sx={{
                    p: 2,
                    textAlign: 'center',
                    bgcolor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                    border: isDark ? '1px solid rgba(255, 255, 255, 0.4)' : '1px solid rgba(0, 0, 0, 0.4)'
                  }}>
                    <Typography variant="h6" fontWeight="700" sx={{ color: isDark ? '#e5e5e5' : '#171717' }}>
                      1️⃣ Express
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      "I want 0.3 ETH on Solana, 0.4 ETH on Polygon"
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={1} sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ color: isDark ? '#e5e5e5' : '#171717' }}>→</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Paper sx={{
                    p: 2,
                    textAlign: 'center',
                    bgcolor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                    border: isDark ? '1px solid rgba(255, 255, 255, 0.4)' : '1px solid rgba(0, 0, 0, 0.4)'
                  }}>
                    <Typography variant="h6" fontWeight="700" sx={{ color: isDark ? '#737373' : '#737373' }}>
                      2️⃣ Receive Keys
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Cryptographic tokens of your intent
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={1} sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ color: isDark ? '#737373' : '#737373' }}>→</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{
                    p: 2,
                    textAlign: 'center',
                    bgcolor: 'rgba(74, 222, 128, 0.2)',
                    border: '1px solid rgba(74, 222, 128, 0.4)'
                  }}>
                    <Typography variant="h6" fontWeight="700" sx={{ color: '#4ade80' }}>
                      3️⃣ Execute Anytime
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Any chain. Any address. Complete privacy.
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Paper>

            {/* Technology Cards */}
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
              Powered by battle-tested cryptography:
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Paper sx={{
                  p: 3,
                  height: '100%',
                  background: isDark
                    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                    : 'linear-gradient(135deg, rgba(0, 0, 0, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                  border: isDark ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(0, 0, 0, 0.3)',
                  transition: 'transform 0.3s',
                  '&:hover': { transform: 'translateY(-8px)' }
                }}>
                  <Lock sx={{ fontSize: 48, color: isDark ? '#e5e5e5' : '#171717', mb: 2 }} />
                  <Typography variant="h6" fontWeight="600" gutterBottom>
                    ECDSA One-Time Signatures
                  </Typography>
                  <Typography color="text.secondary">
                    Each key can only be used once. Prevents tracking and ensures perfect forward secrecy.
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{
                  p: 3,
                  height: '100%',
                  background: isDark
                    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                    : 'linear-gradient(135deg, rgba(0, 0, 0, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                  border: isDark ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(0, 0, 0, 0.3)',
                  transition: 'transform 0.3s',
                  '&:hover': { transform: 'translateY(-8px)' }
                }}>
                  <Security sx={{ fontSize: 48, color: isDark ? '#e5e5e5' : '#171717', mb: 2 }} />
                  <Typography variant="h6" fontWeight="600" gutterBottom>
                    Merkle Tree Verification
                  </Typography>
                  <Typography color="text.secondary">
                    Efficiently proves key ownership without revealing which key from the set you're using.
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{
                  p: 3,
                  height: '100%',
                  background: isDark
                    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                    : 'linear-gradient(135deg, rgba(0, 0, 0, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                  border: isDark ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(0, 0, 0, 0.3)',
                  transition: 'transform 0.3s',
                  '&:hover': { transform: 'translateY(-8px)' }
                }}>
                  <Groups sx={{ fontSize: 48, color: isDark ? '#e5e5e5' : '#171717', mb: 2 }} />
                  <Typography variant="h6" fontWeight="600" gutterBottom>
                    Threshold Cryptography
                  </Typography>
                  <Typography color="text.secondary">
                    Distributed key generation ensures no single party can compromise your privacy.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </SlideContainer>
        );

      case 'howItWorks':
        return (
          <SlideContainer>
            <Typography variant="h3" fontWeight="700" gutterBottom sx={{ mb: 6 }}>
              {slide.title}
            </Typography>
            <Stack spacing={4}>
              {[
                {
                  step: '1',
                  icon: <AccountBalance />,
                  title: 'Deposit Assets',
                  description: 'Send ETH, BNB, or other tokens to the InterNull smart contract on any supported chain.',
                  color: isDark ? '#e5e5e5' : '#171717',
                },
                {
                  step: '2',
                  icon: <AutoAwesome />,
                  title: 'Request Privacy Keys',
                  description: 'System generates unique one-time signature keys for your deposit using distributed threshold cryptography.',
                  color: isDark ? '#a3a3a3' : '#525252',
                },
                {
                  step: '3',
                  icon: <SwapHoriz />,
                  title: 'Withdraw Anywhere, Anytime',
                  description: 'Use your keys to withdraw to a fresh address on the same chain or cross-chain. No time limits. Complete anonymity.',
                  color: isDark ? '#737373' : '#737373',
                },
              ].map((item) => (
                <Paper
                  key={item.step}
                  sx={{
                    p: 4,
                    display: 'flex',
                    gap: 3,
                    background: `linear-gradient(135deg, ${alpha(item.color, 0.1)} 0%, ${alpha(item.color, 0.05)} 100%)`,
                    border: `2px solid ${alpha(item.color, 0.3)}`,
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateX(10px)',
                      borderColor: item.color,
                    }
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: item.color,
                      width: 64,
                      height: 64,
                      fontSize: 28,
                      fontWeight: 700,
                    }}
                  >
                    {item.step}
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="h5" fontWeight="600" gutterBottom sx={{ color: item.color }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {item.description}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', color: item.color }}>
                    {item.icon}
                  </Box>
                </Paper>
              ))}
            </Stack>
          </SlideContainer>
        );

      case 'technology':
        return (
          <SlideContainer>
            <Typography variant="h3" fontWeight="700" gutterBottom sx={{ mb: 4 }}>
              {slide.title}
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Typography variant="h5" fontWeight="600" gutterBottom sx={{ color: isDark ? '#e5e5e5' : '#171717' }}>
                  What Makes Us Different
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon><CheckCircle sx={{ color: '#4ade80' }} /></ListItemIcon>
                    <ListItemText
                      primary="No Trusted Setup Required"
                      secondary="Unlike zk-SNARKs, our system needs no ceremony or trust assumptions"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircle sx={{ color: '#4ade80' }} /></ListItemIcon>
                    <ListItemText
                      primary="Instant Withdrawals"
                      secondary="No relayer delays or waiting periods. Withdraw immediately."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircle sx={{ color: '#4ade80' }} /></ListItemIcon>
                    <ListItemText
                      primary="Keys Never Expire"
                      secondary="Hold your keys for years. No time pressure to withdraw."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircle sx={{ color: '#4ade80' }} /></ListItemIcon>
                    <ListItemText
                      primary="Native Cross-Chain Support"
                      secondary="Deposit on Ethereum, withdraw on BNB Chain. Built-in from day one."
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h5" fontWeight="600" gutterBottom sx={{ color: isDark ? '#e5e5e5' : '#171717', mb: 3 }}>
                  Technical Advantages
                </Typography>
                <Stack spacing={2}>
                  {[
                    { label: 'Gas Efficiency', value: '~200k gas', icon: <Speed /> },
                    { label: 'Security Model', value: 'Ethereum-grade', icon: <Shield /> },
                    { label: 'Anonymity Set', value: 'Unlimited growth', icon: <Groups /> },
                    { label: 'Merkle Proof Size', value: '~500 bytes', icon: <Fingerprint /> },
                  ].map((stat) => (
                    <Paper
                      key={stat.label}
                      sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                        border: isDark ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.2)',
                      }}
                    >
                      <Box sx={{ color: isDark ? '#e5e5e5' : '#171717' }}>{stat.icon}</Box>
                      <Box flex={1}>
                        <Typography variant="body2" color="text.secondary">
                          {stat.label}
                        </Typography>
                        <Typography variant="h6" fontWeight="600">
                          {stat.value}
                        </Typography>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </SlideContainer>
        );

      case 'features':
        return (
          <SlideContainer>
            <Typography variant="h3" fontWeight="700" gutterBottom sx={{ mb: 6, textAlign: 'center' }}>
              {slide.title}
            </Typography>
            <Grid container spacing={3}>
              {[
                {
                  icon: <LinkOff />,
                  title: 'Unlinkable Transactions',
                  description: 'Mathematical impossibility to link deposits to withdrawals',
                  color: isDark ? '#e5e5e5' : '#171717',
                },
                {
                  icon: <Language />,
                  title: 'True Cross-Chain',
                  description: 'Deposit on one chain, withdraw on another. No bridges needed.',
                  color: isDark ? '#a3a3a3' : '#525252',
                },
                {
                  icon: <Timer />,
                  title: 'No Expiration',
                  description: 'Keys remain valid forever. Withdraw when you want.',
                  color: isDark ? '#737373' : '#737373',
                },
                {
                  icon: <Speed />,
                  title: 'Instant Execution',
                  description: 'No relayer delays. Direct smart contract interaction.',
                  color: isDark ? '#e5e5e5' : '#171717',
                },
                {
                  icon: <Shield />,
                  title: 'Battle-Tested Crypto',
                  description: 'Built on ECDSA, the same crypto securing Bitcoin & Ethereum.',
                  color: isDark ? '#a3a3a3' : '#525252',
                },
                {
                  icon: <Public />,
                  title: 'Open Source',
                  description: 'Fully auditable code. No hidden backdoors.',
                  color: isDark ? '#737373' : '#737373',
                },
              ].map((feature, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card
                    sx={{
                      height: '100%',
                      background: `linear-gradient(135deg, ${alpha(feature.color, 0.1)} 0%, ${alpha(feature.color, 0.05)} 100%)`,
                      border: `2px solid ${alpha(feature.color, 0.3)}`,
                      transition: 'all 0.3s',
                      '&:hover': {
                        transform: 'translateY(-8px) scale(1.02)',
                        borderColor: feature.color,
                        boxShadow: `0 20px 40px ${alpha(feature.color, 0.3)}`,
                      }
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', p: 4 }}>
                      <Box sx={{ color: feature.color, mb: 2 }}>
                        {React.cloneElement(feature.icon, { sx: { fontSize: 56 } })}
                      </Box>
                      <Typography variant="h6" fontWeight="600" gutterBottom>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </SlideContainer>
        );

      case 'market':
        return (
          <SlideContainer>
            <Typography variant="overline" sx={{ color: isDark ? '#a78bfa' : '#7c3aed', letterSpacing: 4, mb: 1, display: 'block', textAlign: 'center' }}>
              MARKET OPPORTUNITY
            </Typography>
            <Typography variant="h3" fontWeight="700" gutterBottom sx={{ mb: 4, textAlign: 'center' }}>
              Total Addressable <span style={{ color: '#22d3ee' }}>Market</span>
            </Typography>
            <Grid container spacing={4}>
              {/* Left side - Privacy Protocol Market Chart */}
              <Grid item xs={12} md={7}>
                <Paper sx={{ p: 4, bgcolor: 'rgba(13, 17, 40, 0.8)', border: isDark ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(0, 0, 0, 0.3)' }}>
                  <Typography variant="h5" fontWeight="600" gutterBottom sx={{ color: isDark ? '#e5e5e5' : '#171717', mb: 3 }}>
                    Privacy Protocols Volume Comparison
                  </Typography>
                  <Stack spacing={3}>
                    {/* Tornado Cash Bar */}
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body1" fontWeight="600" sx={{ color: '#4ade80' }}>
                          🌪️ Tornado Cash
                        </Typography>
                        <Typography variant="body1" fontWeight="700" sx={{ color: '#4ade80' }}>
                          $1.9B
                        </Typography>
                      </Box>
                      <Box sx={{
                        height: 32,
                        background: 'linear-gradient(90deg, #4ade80 0%, #22c55e 100%)',
                        borderRadius: 2,
                        width: '100%',
                        boxShadow: '0 4px 20px rgba(74, 222, 128, 0.4)',
                      }} />
                      <Typography variant="caption" color="text.secondary">
                        H1 2024 deposits (+50% YoY)
                      </Typography>
                    </Box>

                    {/* Monero Bar */}
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body1" fontWeight="600" sx={{ color: '#f59e0b' }}>
                          🔒 Monero (XMR)
                        </Typography>
                        <Typography variant="body1" fontWeight="700" sx={{ color: '#f59e0b' }}>
                          $3.5B
                        </Typography>
                      </Box>
                      <Box sx={{
                        height: 32,
                        background: 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)',
                        borderRadius: 2,
                        width: '85%',
                        boxShadow: '0 4px 20px rgba(245, 158, 11, 0.4)',
                      }} />
                      <Typography variant="caption" color="text.secondary">
                        Market Cap (+17% in 2024)
                      </Typography>
                    </Box>

                    {/* Zcash Bar */}
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body1" fontWeight="600" sx={{ color: isDark ? '#e5e5e5' : '#171717' }}>
                          🛡️ Zcash (ZEC)
                        </Typography>
                        <Typography variant="body1" fontWeight="700" sx={{ color: isDark ? '#e5e5e5' : '#171717' }}>
                          $800M+
                        </Typography>
                      </Box>
                      <Box sx={{
                        height: 32,
                        background: isDark
                          ? 'linear-gradient(90deg, #e5e5e5 0%, #a3a3a3 100%)'
                          : 'linear-gradient(90deg, #171717 0%, #525252 100%)',
                        borderRadius: 2,
                        width: '45%',
                        boxShadow: isDark ? '0 4px 20px rgba(255, 255, 255, 0.4)' : '0 4px 20px rgba(0, 0, 0, 0.4)',
                      }} />
                      <Typography variant="caption" color="text.secondary">
                        Daily volume (+112% growth in 2024)
                      </Typography>
                    </Box>
                  </Stack>

                  {/* The Gap */}
                  <Paper sx={{
                    mt: 4,
                    p: 2,
                    background: isDark
                      ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.1) 100%)'
                      : 'linear-gradient(135deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.1) 100%)',
                    border: isDark ? '2px dashed rgba(255, 255, 255, 0.5)' : '2px dashed rgba(0, 0, 0, 0.5)',
                    textAlign: 'center'
                  }}>
                    <Typography variant="h6" fontWeight="700" sx={{ color: isDark ? '#737373' : '#737373' }}>
                      🚀 The Market Gap: Cross-Chain + Privacy + Intents
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      No protocol combines deferred execution, multi-chain support, and unlinkable transactions
                    </Typography>
                  </Paper>
                </Paper>
              </Grid>

              {/* Right side - Market Stats */}
              <Grid item xs={12} md={5}>
                <Stack spacing={3}>
                  <Paper sx={{
                    p: 3,
                    background: isDark
                      ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)'
                      : 'linear-gradient(135deg, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0.05) 100%)',
                    border: isDark ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(0, 0, 0, 0.3)',
                    textAlign: 'center'
                  }}>
                    <Typography variant="h2" fontWeight="800" sx={{
                      background: isDark
                        ? 'linear-gradient(90deg, #e5e5e5 0%, #a3a3a3 100%)'
                        : 'linear-gradient(90deg, #171717 0%, #525252 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}>
                      $3T+
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      DeFi Total Market Cap
                    </Typography>
                  </Paper>

                  <Paper sx={{
                    p: 3,
                    background: 'linear-gradient(135deg, rgba(74, 222, 128, 0.15) 0%, rgba(74, 222, 128, 0.05) 100%)',
                    border: '1px solid rgba(74, 222, 128, 0.3)',
                    textAlign: 'center'
                  }}>
                    <Typography variant="h2" fontWeight="800" sx={{ color: '#4ade80' }}>
                      500M+
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Crypto Users Globally
                    </Typography>
                  </Paper>

                  <Paper sx={{
                    p: 3,
                    background: isDark
                      ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)'
                      : 'linear-gradient(135deg, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0.05) 100%)',
                    border: isDark ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(0, 0, 0, 0.3)',
                    textAlign: 'center'
                  }}>
                    <Typography variant="h2" fontWeight="800" sx={{ color: isDark ? '#737373' : '#737373' }}>
                      $5B+
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Privacy Protocol Volume (2024)
                    </Typography>
                  </Paper>

                  <Chip
                    label="📈 Privacy demand growing 50%+ YoY"
                    sx={{
                      bgcolor: 'rgba(74, 222, 128, 0.2)',
                      color: '#4ade80',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      py: 2
                    }}
                  />
                </Stack>
              </Grid>
            </Grid>
          </SlideContainer>
        );

      case 'traction':
        return (
          <SlideContainer>
            <Typography variant="overline" sx={{ color: isDark ? '#a78bfa' : '#7c3aed', letterSpacing: 4, mb: 1, display: 'block', textAlign: 'center' }}>
              TRACTION
            </Typography>
            <Typography variant="h3" fontWeight="700" gutterBottom sx={{ mb: 6, textAlign: 'center' }}>
              Live on <span style={{ color: '#22d3ee' }}>Testnet</span>
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {[
                { value: '6', label: 'CHAINS SUPPORTED' },
                { value: '14', label: 'TOKENS SUPPORTED' },
                { value: '5', label: 'DKG NODES LIVE' },
                { value: '18', label: 'MCP AGENT TOOLS' },
                { value: '3/5', label: 'THRESHOLD SECURITY' },
                { value: '800+', label: 'TRANSACTIONS PROCESSED' },
              ].map((stat, index) => (
                <Grid item xs={6} md={4} key={index}>
                  <Paper sx={{
                    p: 4,
                    textAlign: 'center',
                    background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)',
                    border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
                    borderRadius: 2,
                  }}>
                    <Typography variant="h2" fontWeight="800" sx={{ color: '#0ea5e9', mb: 1 }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 2, fontWeight: 600 }}>
                      {stat.label}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
              {['E2E Tests Passing', 'Cross-chain Verified', 'Edge Cases Covered', 'npm Published'].map((tag) => (
                <Chip key={tag} label={tag} sx={{
                  bgcolor: isDark ? 'rgba(139, 92, 246, 0.15)' : 'rgba(124, 58, 237, 0.1)',
                  color: isDark ? '#a78bfa' : '#7c3aed',
                  fontWeight: 600,
                  border: isDark ? '1px solid rgba(139, 92, 246, 0.3)' : '1px solid rgba(124, 58, 237, 0.3)',
                }} />
              ))}
            </Stack>
          </SlideContainer>
        );

      case 'cta':
        return (
          <SlideContainer>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h2" fontWeight="700" gutterBottom sx={{
                background: isDark
                  ? 'linear-gradient(90deg, #e5e5e5 0%, #a3a3a3 100%)'
                  : 'linear-gradient(90deg, #171717 0%, #525252 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 3
              }}>
                {slide.title}
              </Typography>
              <Typography variant="h5" color="text.secondary" paragraph sx={{ mb: 6, maxWidth: 800, mx: 'auto' }}>
                The future of finance demands privacy. InterNull makes it possible.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center" sx={{ mb: 6 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/dashboard')}
                  endIcon={<ArrowForward />}
                  sx={{
                    bgcolor: isDark ? '#e5e5e5' : '#171717',
                    '&:hover': { bgcolor: '#5a67d8' },
                    px: 4,
                    py: 2,
                    fontSize: '1.1rem',
                  }}
                >
                  Launch App
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/docs')}
                  startIcon={<Article />}
                  sx={{
                    borderColor: isDark ? '#e5e5e5' : '#171717',
                    color: isDark ? '#e5e5e5' : '#171717',
                    px: 4,
                    py: 2,
                    fontSize: '1.1rem',
                    '&:hover': {
                      borderColor: '#5a67d8',
                      bgcolor: isDark ? alpha('#e5e5e5', 0.1) : alpha('#171717', 0.1),
                    }
                  }}
                >
                  Read Documentation
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => window.open('https://github.com/InterNullOrg', '_blank')}
                  startIcon={<GitHub />}
                  sx={{
                    borderColor: isDark ? '#e5e5e5' : '#171717',
                    color: isDark ? '#e5e5e5' : '#171717',
                    px: 4,
                    py: 2,
                    fontSize: '1.1rem',
                    '&:hover': {
                      borderColor: '#5a67d8',
                      bgcolor: isDark ? alpha('#e5e5e5', 0.1) : alpha('#171717', 0.1),
                    }
                  }}
                >
                  View on GitHub
                </Button>
              </Stack>

              <Divider sx={{ my: 6 }} />

              <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                "Privacy is not about having something to hide. Privacy is about protecting who you are."
              </Typography>
            </Box>
          </SlideContainer>
        );

      case 'protocolInAction':
        return (
          <SlideContainer>
            <Typography variant="overline" sx={{ color: isDark ? '#a78bfa' : '#7c3aed', letterSpacing: 4, mb: 1, display: 'block', textAlign: 'center' }}>
              PROTOCOL IN ACTION
            </Typography>
            <Typography variant="h3" fontWeight="700" gutterBottom sx={{ mb: 6, textAlign: 'center' }}>
              Split, Route, <span style={{ color: '#22d3ee' }}>Disappear</span>
            </Typography>
            <Grid container spacing={4}>
              {[
                {
                  label: 'EXAMPLE 1',
                  deposit: { chain: 'Ethereum', amount: '1 ETH', addr: '0x1a2B...7f3E' },
                  withdrawals: [
                    { chain: 'Ethereum', amount: '0.5 ETH', addr: '0x9cD4...2a1B' },
                    { chain: 'Base', amount: '0.5 ETH', addr: '0xFe82...5d9A' },
                  ],
                  note: 'No on-chain link between deposit & withdrawals',
                },
                {
                  label: 'EXAMPLE 2',
                  deposit: { chain: 'Solana', amount: '100 USDC', addr: '7xKp...mN4q' },
                  withdrawals: [
                    { chain: 'Solana', amount: '30 USDC', addr: '3bRz...vY8w' },
                    { chain: 'Polygon', amount: '30 USDC', addr: '0x44Ae...8c3F' },
                    { chain: 'Hyperliquid', amount: '40 USDC', addr: '0x8Bb17...6eD2' },
                  ],
                  note: '1 deposit → 3 chains, 3 fresh addresses',
                },
              ].map((example, idx) => (
                <Grid item xs={12} md={6} key={idx}>
                  <Paper sx={{ p: 3, background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)', border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' }}>
                    <Typography variant="overline" sx={{ color: isDark ? '#a78bfa' : '#7c3aed', letterSpacing: 3 }}>{example.label}</Typography>
                    <Paper sx={{ p: 2, mt: 1, mb: 2, textAlign: 'center', bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' }}>
                      <Typography variant="caption" color="text.secondary">Deposit on {example.deposit.chain}</Typography>
                      <Typography variant="h5" fontWeight="700">{example.deposit.amount}</Typography>
                      <Typography variant="caption" color="text.secondary">{example.deposit.addr}</Typography>
                    </Paper>
                    <Typography variant="h5" sx={{ textAlign: 'center', color: isDark ? '#a78bfa' : '#7c3aed', mb: 2 }}>↓</Typography>
                    <Grid container spacing={1}>
                      {example.withdrawals.map((w, i) => (
                        <Grid item xs={12 / example.withdrawals.length} key={i}>
                          <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'rgba(34,211,238,0.08)', border: '1px solid rgba(34,211,238,0.3)' }}>
                            <Typography variant="caption" color="text.secondary">{w.chain}</Typography>
                            <Typography variant="subtitle1" fontWeight="700">{w.amount}</Typography>
                            <Typography variant="caption" sx={{ color: '#22d3ee' }}>{w.addr}</Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>✓ {example.note}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </SlideContainer>
        );

      case 'architecture':
        return (
          <SlideContainer>
            <Typography variant="overline" sx={{ color: isDark ? '#a78bfa' : '#7c3aed', letterSpacing: 4, mb: 1, display: 'block', textAlign: 'center' }}>
              ARCHITECTURE
            </Typography>
            <Typography variant="h3" fontWeight="700" gutterBottom sx={{ mb: 6, textAlign: 'center' }}>
              Decentralized <span style={{ color: '#22d3ee' }}>5-Node</span> Network
            </Typography>
            <Stack spacing={2} sx={{ maxWidth: 800, mx: 'auto', mb: 4 }}>
              {[
                { label: 'Treasury Smart Contracts (deployed per chain)', color: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)', border: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)' },
                { label: 'Distributed Nodes (3-of-5 Threshold)', color: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', border: isDark ? 'rgba(139,92,246,0.3)' : 'rgba(124,58,237,0.3)' },
                { label: 'P2P Secure Share Exchange (V2 Protocol — HMAC + Mini-VSS)', color: 'rgba(34,211,238,0.08)', border: 'rgba(34,211,238,0.4)' },
              ].map((layer, i) => (
                <Paper key={i} sx={{ p: 2.5, textAlign: 'center', bgcolor: layer.color, border: `1px solid ${layer.border}` }}>
                  <Typography variant="body1" fontWeight="600">{layer.label}</Typography>
                </Paper>
              ))}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' }}>
                    <Typography variant="subtitle1" fontWeight="600">Client / Agent</Typography>
                    <Typography variant="caption" color="text.secondary">Key Reconstruction</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' }}>
                    <Typography variant="subtitle1" fontWeight="600">MCP Server (blackbox-mcp)</Typography>
                    <Typography variant="caption" color="text.secondary">npm install & interact</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Stack>
            <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
              {['Feldman VSS', '3-of-5 Threshold', 'ECDSA secp256k1', 'No Trusted Dealer', 'V2 Secure Protocol'].map((tag) => (
                <Chip key={tag} label={tag} sx={{
                  bgcolor: isDark ? 'rgba(139,92,246,0.15)' : 'rgba(124,58,237,0.1)',
                  color: isDark ? '#a78bfa' : '#7c3aed',
                  fontWeight: 600,
                  border: isDark ? '1px solid rgba(139,92,246,0.3)' : '1px solid rgba(124,58,237,0.3)',
                }} />
              ))}
            </Stack>
          </SlideContainer>
        );

      case 'security':
        return (
          <SlideContainer>
            <Typography variant="overline" sx={{ color: isDark ? '#a78bfa' : '#7c3aed', letterSpacing: 4, mb: 1, display: 'block', textAlign: 'center' }}>
              SECURITY MODEL
            </Typography>
            <Typography variant="h3" fontWeight="700" gutterBottom sx={{ mb: 6, textAlign: 'center' }}>
              Zero Trust, <span style={{ color: '#22d3ee' }}>Full Privacy</span>
            </Typography>
            <Grid container spacing={3}>
              {[
                { title: 'No Single Point of Failure', desc: '3-of-5 threshold means up to 2 nodes can go offline or be compromised without affecting the system.' },
                { title: 'No Full Key Exposure', desc: 'Master key never exists in one place. Child keys are derived via one-way HMAC functions. Reconstructed keys exist only client-side, momentarily.' },
                { title: 'On-Chain Enforcement', desc: 'Merkle proofs, nullifiers, and signature verification are all enforced by immutable smart contracts. No off-chain trust required.' },
                { title: 'Self-Custodied Keys', desc: 'Agent wallets are created and stored locally with AES-256-GCM encryption. Private keys never leave the user\'s machine.' },
                { title: 'Idempotent Claims', desc: 'Re-claiming the same deposit returns the same key. Combined with on-chain nullifiers, double-spend is impossible.' },
                { title: 'Fresh Key Per Withdrawal', desc: 'Each withdrawal uses a unique one-time key with no on-chain link to the depositor\'s address.' },
              ].map((item, i) => (
                <Grid item xs={12} md={4} key={i}>
                  <Paper sx={{
                    p: 3, height: '100%',
                    background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                    border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                  }}>
                    <Typography variant="h6" fontWeight="700" gutterBottom>{item.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{item.desc}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </SlideContainer>
        );

      case 'competitive':
        return (
          <SlideContainer>
            <Typography variant="overline" sx={{ color: isDark ? '#a78bfa' : '#7c3aed', letterSpacing: 4, mb: 1, display: 'block', textAlign: 'center' }}>
              COMPETITIVE LANDSCAPE
            </Typography>
            <Typography variant="h3" fontWeight="700" gutterBottom sx={{ mb: 4, textAlign: 'center' }}>
              How InterNull <span style={{ color: '#22d3ee' }}>Compares</span>
            </Typography>
            <Paper sx={{ p: 3, overflow: 'auto', bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' }}>
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& th, & td': { p: 1.5, textAlign: 'left', borderBottom: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)' }, '& th': { color: 'text.secondary', fontWeight: 600, fontSize: '0.75rem', letterSpacing: 2, textTransform: 'uppercase' } }}>
                <thead>
                  <tr>
                    <th>Feature</th><th>Tornado Cash</th><th>Railgun</th><th>Zcash</th><th>Monero</th><th style={{ color: '#22d3ee' }}>InterNull</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Multi-chain', '✓ Multiple EVM', '✓ Multiple EVM', '✗ Own chain', '✗ Own chain', '✓ 6 chains + Solana'],
                    ['Cross-chain privacy', '✗', '✗', '✗', '✗', '✓ Native'],
                    ['AI Agent Ready', '✗', '✗', '✗', '✗', '✓ MCP Server'],
                    ['Smart contracts', '✓ EVM', '✓ EVM', '✗', '✗', '✓ EVM + Solana'],
                    ['No trusted setup', '~ Ceremony', '✓', '~ Sapling', '✓', '✓ DKG'],
                    ['Programmable / API', '✗', '~ Limited', '✗', '✗', '✓ Full API + MCP'],
                    ['Gas-free withdrawals', '✗', '~ Relayer', '~ Own chain', '~ Own chain', '✓ Relay service'],
                  ].map((row, i) => (
                    <tr key={i}>
                      <td><Typography variant="body2" fontWeight="600">{row[0]}</Typography></td>
                      {row.slice(1).map((cell, j) => (
                        <td key={j}><Typography variant="body2" sx={{ color: cell.startsWith('✓') ? '#4ade80' : cell.startsWith('✗') ? '#ef4444' : '#f59e0b' }}>{cell}</Typography></td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Paper>
          </SlideContainer>
        );

      case 'agents':
        return (
          <SlideContainer>
            <Typography variant="overline" sx={{ color: isDark ? '#a78bfa' : '#7c3aed', letterSpacing: 4, mb: 1, display: 'block', textAlign: 'center' }}>
              AI-NATIVE INFRASTRUCTURE
            </Typography>
            <Typography variant="h3" fontWeight="700" gutterBottom sx={{ mb: 2, textAlign: 'center' }}>
              Built for <span style={{ color: '#22d3ee' }}>AI Agents</span>
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mb: 4, maxWidth: 700, mx: 'auto' }}>
              InterNull is the first privacy protocol with a native MCP (Model Context Protocol) server. Any AI agent can deposit, claim keys, and withdraw with a single npm package.
            </Typography>
            <Paper sx={{ p: 4, mb: 4, maxWidth: 700, mx: 'auto', bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)', border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)', borderRadius: 2 }}>
              <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.9rem', color: 'text.secondary', textAlign: 'center', whiteSpace: 'pre-wrap' }}>
{`// Any AI agent's .mcp.json — zero config
{
  "mcpServers": {
    "internull": {
      "command": "npx",
      "args": ["blackbox-mcp"]
    }
  }
}`}
              </Typography>
            </Paper>
            <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
              {['npm: blackbox-mcp', '18 MCP Tools', 'Self-Custodied Wallets', 'Works with Claude, GPT, any MCP client'].map((tag) => (
                <Chip key={tag} label={tag} sx={{
                  bgcolor: isDark ? 'rgba(139,92,246,0.15)' : 'rgba(124,58,237,0.1)',
                  color: isDark ? '#a78bfa' : '#7c3aed',
                  fontWeight: 600,
                  border: isDark ? '1px solid rgba(139,92,246,0.3)' : '1px solid rgba(124,58,237,0.3)',
                }} />
              ))}
            </Stack>
          </SlideContainer>
        );

      case 'useCases':
        return (
          <SlideContainer>
            <Typography variant="overline" sx={{ color: isDark ? '#a78bfa' : '#7c3aed', letterSpacing: 4, mb: 1, display: 'block', textAlign: 'center' }}>
              USE CASES
            </Typography>
            <Typography variant="h3" fontWeight="700" gutterBottom sx={{ mb: 6, textAlign: 'center' }}>
              Who Needs <span style={{ color: '#22d3ee' }}>InterNull</span>?
            </Typography>
            <Grid container spacing={3}>
              {[
                { title: 'AI Agent Treasuries', desc: 'Autonomous agents managing multi-chain portfolios need private fund movement to prevent front-running and strategy leaking.' },
                { title: 'DAO Operations', desc: "DAOs executing strategic token purchases, payroll, or grants without broadcasting intent to the entire market." },
                { title: 'Cross-Chain DeFi', desc: 'Move liquidity between chains without leaving a traceable bridge trail. Deposit on L1, withdraw on L2.' },
                { title: 'Personal Privacy', desc: "Individuals who simply don't want their entire financial history public. Salary, savings, purchases — all visible today." },
              ].map((item, i) => (
                <Grid item xs={12} md={6} key={i}>
                  <Paper sx={{
                    p: 4, height: '100%',
                    background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                    border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                  }}>
                    <Typography variant="h6" fontWeight="700" gutterBottom>{item.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{item.desc}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </SlideContainer>
        );

      case 'roadmap':
        return (
          <SlideContainer>
            <Typography variant="overline" sx={{ color: isDark ? '#a78bfa' : '#7c3aed', letterSpacing: 4, mb: 1, display: 'block', textAlign: 'center' }}>
              ROADMAP
            </Typography>
            <Typography variant="h3" fontWeight="700" gutterBottom sx={{ mb: 6, textAlign: 'center' }}>
              What's <span style={{ color: '#22d3ee' }}>Next</span>
            </Typography>
            <Grid container spacing={3}>
              {[
                { phase: 'Phase 1 — Complete', color: '#4ade80', items: ['Testnet live across 6 chains', 'V2 secure protocol deployed', 'MCP server published on npm', 'Full E2E test coverage', 'Relay withdrawal service', '14 tokens, 6 chains'] },
                { phase: 'Phase 2 — Q2 2026', color: isDark ? '#a78bfa' : '#7c3aed', items: ['Smart contract security audit', 'Mainnet launch', 'Additional EVM chains', 'More token integrations', 'Compliance SDK', 'Institutional onboarding'] },
                { phase: 'Phase 3 — Q4 2026', color: '#22d3ee', items: ['Scale to 10 DKG nodes', '7-of-10 threshold security', 'Decentralized node operators', 'Cross-chain atomic swaps', 'Private DeFi composability', 'Agent-to-agent private transfers'] },
              ].map((phase, i) => (
                <Grid item xs={12} md={4} key={i}>
                  <Paper sx={{
                    p: 3, height: '100%',
                    background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                    border: `1px solid ${alpha(phase.color, 0.3)}`,
                  }}>
                    <Typography variant="h6" fontWeight="700" sx={{ color: phase.color, mb: 2 }}>{phase.phase}</Typography>
                    <Stack spacing={1}>
                      {phase.items.map((item, j) => (
                        <Typography key={j} variant="body2" color="text.secondary">{item}</Typography>
                      ))}
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </SlideContainer>
        );

      case 'team':
        return (
          <SlideContainer>
            <Typography variant="overline" sx={{ color: isDark ? '#a78bfa' : '#7c3aed', letterSpacing: 4, mb: 1, display: 'block', textAlign: 'center' }}>
              THE TEAM
            </Typography>
            <Typography variant="h3" fontWeight="700" gutterBottom sx={{ mb: 6, textAlign: 'center' }}>
              Built by <span style={{ color: '#22d3ee' }}>InterNull</span>
            </Typography>
            <Grid container spacing={3}>
              {[
                { name: 'Jamshed Memon', role: 'Technology', desc: '+15 years academic & industry expertise. +25 research papers, 4 patents across Information Security, Cryptography, and Blockchain.' },
                { name: 'Raul Calvo. S', role: 'Legal', desc: '+20 years in law and technology. Legal expertise in deploying DeFi and privacy solutions in Web3.' },
                { name: 'Daniel Dohne', role: 'Product', desc: '+10 years as a product and software dev. Founded cross-chain infrastructure processing +$1M.' },
                { name: 'Ian Poirier', role: 'Strategy', desc: '+10 years in strategy consulting & start-ups. Grew Toucan Protocol to +$2b trading volume.' },
                { name: 'Niko Petrov', role: 'Marketing', desc: '+7 years in DeFi growth, scaling brands from 0 to 200k+ followers and +150k community members.' },
              ].map((member, i) => (
                <Grid item xs={12} sm={6} md={2.4} key={i}>
                  <Paper sx={{
                    p: 2.5, textAlign: 'center', height: '100%',
                    background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                    border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                  }}>
                    <Typography variant="overline" sx={{ color: ['#4ade80', '#a78bfa', '#f59e0b', '#ef4444', '#22d3ee'][i], letterSpacing: 2 }}>{member.role}</Typography>
                    <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 1 }}>{member.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{member.desc}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" sx={{ mt: 4 }}>
              {['Cross-Functional Team', 'AI-Augmented Development', 'DeFi + Privacy Expertise', 'Legal & Compliance'].map((tag) => (
                <Chip key={tag} label={tag} sx={{
                  bgcolor: isDark ? 'rgba(139,92,246,0.15)' : 'rgba(124,58,237,0.1)',
                  color: isDark ? '#a78bfa' : '#7c3aed',
                  fontWeight: 600,
                  border: isDark ? '1px solid rgba(139,92,246,0.3)' : '1px solid rgba(124,58,237,0.3)',
                }} />
              ))}
            </Stack>
          </SlideContainer>
        );

      default:
        // Title slide
        return (
          <SlideContainer>
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '3rem', md: '5rem' },
                  background: isDark
                    ? 'linear-gradient(90deg, #e5e5e5 0%, #a3a3a3 100%)'
                    : 'linear-gradient(90deg, #171717 0%, #525252 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 3,
                }}
              >
                {slide.title}
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 500,
                  color: 'text.secondary',
                  mb: 2,
                }}
              >
                {slide.subtitle}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: isDark ? '#e5e5e5' : '#171717',
                  fontStyle: 'italic',
                  mb: 6,
                }}
              >
                {slide.tagline}
              </Typography>

              <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 8 }}>
                <Chip icon={<Lock />} label="Private" sx={{ bgcolor: isDark ? alpha('#e5e5e5', 0.2) : alpha('#171717', 0.2), color: isDark ? '#e5e5e5' : '#171717' }} />
                <Chip icon={<Language />} label="Cross-Chain" sx={{ bgcolor: isDark ? alpha('#a3a3a3', 0.2) : alpha('#525252', 0.2), color: isDark ? '#a3a3a3' : '#525252' }} />
                <Chip icon={<Speed />} label="Instant" sx={{ bgcolor: isDark ? alpha('#737373', 0.2) : alpha('#737373', 0.2), color: isDark ? '#737373' : '#737373' }} />
              </Stack>

              <Button
                variant="contained"
                size="large"
                onClick={nextSlide}
                endIcon={<ArrowForward />}
                sx={{
                  bgcolor: isDark ? '#e5e5e5' : '#171717',
                  '&:hover': { bgcolor: '#5a67d8' },
                  px: 6,
                  py: 2,
                  fontSize: '1.2rem',
                }}
              >
                Start Presentation
              </Button>
            </Box>
          </SlideContainer>
        );
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: isDark ? '#050505' : '#fafafa',
        background: isDark
          ? `
          radial-gradient(circle at 10% 20%, rgba(255, 255, 255, 0.15) 0%, transparent 50%),
          radial-gradient(circle at 90% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
          linear-gradient(180deg, #050505 0%, #0a0a0a 100%)
        `
          : `
          radial-gradient(circle at 10% 20%, rgba(0, 0, 0, 0.15) 0%, transparent 50%),
          radial-gradient(circle at 90% 80%, rgba(0, 0, 0, 0.1) 0%, transparent 50%),
          linear-gradient(180deg, #fafafa 0%, #ffffff 100%)
        `,
        color: isDark ? '#ffffff' : '#171717',
      }}
    >
      <Header />

      <Container maxWidth="lg" sx={{ pt: 4, pb: 8 }}>
        {renderSlideContent()}

        {/* Navigation */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 6,
          pt: 4,
          borderTop: isDark ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.2)'
        }}>
          <Button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            startIcon={<ArrowBack />}
            sx={{ color: isDark ? '#e5e5e5' : '#171717' }}
          >
            Previous
          </Button>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {slides.map((_, index) => (
              <Box
                key={index}
                onClick={() => setCurrentSlide(index)}
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: index === currentSlide
                    ? (isDark ? '#e5e5e5' : '#171717')
                    : (isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'),
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  '&:hover': {
                    bgcolor: index === currentSlide
                      ? (isDark ? '#e5e5e5' : '#171717')
                      : (isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'),
                    transform: 'scale(1.2)',
                  }
                }}
              />
            ))}
          </Box>

          <Button
            onClick={nextSlide}
            disabled={currentSlide === slides.length - 1}
            endIcon={<ArrowForward />}
            sx={{ color: isDark ? '#e5e5e5' : '#171717' }}
          >
            Next
          </Button>
        </Box>

        {/* Slide counter */}
        <Typography
          variant="body2"
          sx={{
            textAlign: 'center',
            mt: 2,
            color: 'text.secondary',
          }}
        >
          Slide {currentSlide + 1} of {slides.length}
        </Typography>
      </Container>
    </Box>
  );
};

export default PitchDeck;
